<template>
    <ideo-modal
        :model-value="modelValue"
        @update:modelValue="$emit('update:modelValue', $event)"
        modal-class="work-status-modal"
        size="sm"
        hide-header
        hide-footer
        centered
        static
    >
        <div class="work-status" :class="`is-${variant}`">
            <div class="work-status-icon">
                <i :class="isStart ? 'fas fa-briefcase' : 'fas fa-power-off'"></i>
            </div>
            <h5 class="work-status-title">{{ isStart ? $t('[[[Nie jesteś w pracy]]]') : $t('[[[Zakończyć pracę?]]]') }}</h5>
            <p class="work-status-text">
                {{ isStart ? $t('[[[Aby rozpocząć zadanie, musisz mieć włączony status "W pracy".]]]') : $t('[[[Po wyłączeniu statusu nie będziesz mógł rozpoczynać zadań.]]]') }}
            </p>
            <router-link
                v-if="!isStart && taskTitle"
                class="work-status-task"
                :to="taskPublicId ? { name: 'renter-tasks-management-run', params: { publicId: taskPublicId } } : ''"
                :title="$t('[[[Przejdź do zadania]]]')"
                @click="onCancel"
            >
                <i class="fas fa-clock"></i>
                <div>
                    <small>{{ $t('[[[Masz rozpoczęte zadanie:]]]') }}</small>
                    <strong>{{ taskTitle }}</strong>
                </div>
                <i v-if="taskPublicId" class="fas fa-chevron-right work-status-task-go"></i>
            </router-link>
            <p class="work-status-hint">
                {{ isStart ? $t('[[[Status zostanie włączony automatycznie, a zadanie rozpocznie się od razu.]]]') : (taskTitle ? $t('[[[Zadanie pozostanie rozpoczęte. Pamiętaj, aby je zakończyć lub zapauzować.]]]') : '') }}
            </p>
            <div class="work-status-actions">
                <button type="button" class="btn w-100" :class="isStart ? 'btn-success' : 'btn-danger'" @click.stop="onConfirm">
                    <i :class="isStart ? 'fas fa-play' : 'fas fa-power-off'"></i>
                    {{ isStart ? $t('[[[Włącz i rozpocznij zadanie]]]') : $t('[[[Zakończ pracę]]]') }}
                </button>
                <button type="button" class="btn btn-link w-100" @click.stop="onCancel">{{ $t('[[[Anuluj]]]') }}</button>
            </div>
        </div>
    </ideo-modal>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Prop } from '@/helpers/Decorators';

/**
 * Okno informujące o statusie "W pracy":
 *  - variant "start": użytkownik chce rozpocząć zadanie, ale nie jest w pracy (potwierdzenie włącza status),
 *  - variant "end": użytkownik chce zakończyć pracę (opcjonalnie z informacją o zadaniu w toku).
 */
@Options({
    name: 'work-status-modal',
    emits: ['update:modelValue', 'confirm']
})
export default class WorkStatusModal extends Vue
{
    @Prop({ default: false })
    public modelValue: boolean;

    @Prop({ default: 'start', validator: (value: string) => ['start', 'end'].includes(value) })
    public variant: string;

    @Prop({ default: null })
    public taskTitle: string | null;

    @Prop({ default: null })
    public taskPublicId: string | null;

    public get isStart(): boolean
    {
        return this.variant === 'start';
    }

    public onConfirm(): void
    {
        this.$emit('update:modelValue', false);
        this.$emit('confirm');
    }

    public onCancel(): void
    {
        this.$emit('update:modelValue', false);
    }
}
</script>

<style lang="scss">
.work-status-modal {
    .modal-body {
        padding: 2rem 1.75rem 1.5rem;
    }
}

.work-status {
    text-align: center;

    .work-status-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 4rem;
        height: 4rem;
        margin: 0 auto 1.25rem;
        border-radius: 50%;
        font-size: 1.6rem;
    }

    &.is-start .work-status-icon {
        color: var(--success);
        background-color: rgba(46, 184, 92, .15);
    }

    &.is-end .work-status-icon {
        color: var(--danger);
        background-color: rgba(229, 83, 83, .15);
    }

    .work-status-title {
        margin-bottom: .5rem;
        font-weight: 700;
    }

    .work-status-text {
        margin-bottom: 1rem;
        opacity: .85;
    }

    .work-status-task {
        display: flex;
        align-items: center;
        gap: .75rem;
        margin-bottom: 1rem;
        padding: .75rem 1rem;
        border-radius: .5rem;
        text-align: left;
        background-color: rgba(255, 193, 7, .15);
        border-left: 4px solid var(--warning);
        color: inherit;
        text-decoration: none;
        cursor: pointer;
        transition: background-color .15s ease;

        &:hover {
            background-color: rgba(255, 193, 7, .25);
        }

        > div {
            flex: 1;
            min-width: 0;
        }

        .work-status-task-go {
            font-size: .8rem;
            opacity: .6;
        }

        > i {
            font-size: 1.2rem;
            color: var(--warning);
        }

        small, strong {
            display: block;
        }

        small {
            opacity: .75;
        }
    }

    .work-status-hint {
        margin-bottom: 1.5rem;
        font-size: .85rem;
        opacity: .65;

        &:empty {
            display: none;
        }
    }

    .work-status-actions {
        display: flex;
        flex-direction: column;
        gap: .5rem;

        .btn:not(.btn-link) {
            padding: .6rem 1rem;
            font-weight: 600;
        }

        .btn-link {
            opacity: .75;
        }
    }
}
</style>
